import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { CadastroService } from '../../../services/cadastro.service';
import { CriarHistoricoProducao, FuncionarioCargoProducao, HistoricoProducao } from '../../../models/cadastro.model';
import { ErrorModel } from '../../../models/error.model';
import { ParametrosService } from '../../../services/parametros.service';
import { Parametros } from '../../../models/parametros.model';

@Component({
  selector: 'app-controle-producao-manutencao',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './controle-producao-manutencao.component.html',
  styleUrls: ['./controle-producao-manutencao.component.css']
})
export class ControleProducaoManutencaoComponent implements OnInit {
  idControle = 0;
  historico: HistoricoProducao[] = [];
  statusPermitidos: Parametros[] = [];
  statusAtual = '';
  statusJaPassados = new Set<string>();

  modalAberto = false;
  salvandoStep = false;
  funcionariosDisponiveis: FuncionarioCargoProducao[] = [];
  formNovoStep = {
    idFuncionario: null as number | null,
    status: '',
    dataInicio: '',
    dataFim: '',
    observacao: ''
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cadastroService: CadastroService,
    private parametrosService: ParametrosService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.toastr.error('Controle de produção inválido.');
      this.voltar();
      return;
    }

    this.idControle = +id;

    this.loadDados();
  }

  loadDados(): void {
    forkJoin({
      status: this.parametrosService.getParametrosByCategorias(['STATUS_PRODUCAO']),
      historico: this.cadastroService.getHistoricoProducao(this.idControle)
    }).subscribe({
      next: ({ status, historico }) => {
        this.statusPermitidos = [...status].sort((a, b) =>
          Number(a.valorNumerico || 0) - Number(b.valorNumerico || 0)
        );

        this.historico = [...historico].sort((a, b) => {
          if (a.ordem !== b.ordem) return a.ordem - b.ordem;
          return new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime();
        });

        const statusNoHistorico = this.historico.map(h => (h.status || '').trim().toUpperCase());
        this.statusJaPassados = new Set(statusNoHistorico);
        this.statusAtual = statusNoHistorico.length > 0 ? statusNoHistorico[statusNoHistorico.length - 1] : '';
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao carregar dados de produção.');
      }
    });
  }

  isStatusAtual(descricao: string): boolean {
    return this.statusAtual === (descricao || '').trim().toUpperCase();
  }

  isStatusConcluido(descricao: string): boolean {
    if (!this.statusAtual || this.statusPermitidos.length === 0) return false;

    const normalizado = (descricao || '').trim().toUpperCase();
    const idxAtual = this.statusPermitidos.findIndex(
      s => (s.descricao || '').trim().toUpperCase() === this.statusAtual
    );
    const idxEste = this.statusPermitidos.findIndex(
      s => (s.descricao || '').trim().toUpperCase() === normalizado
    );

    return idxEste < idxAtual;
  }

  private normalizarStatus(status: string): string {
    return (status || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  podeAvancarStep(): boolean {
    if (!this.statusAtual) {
      return true;
    }
    const statusAtualNormalizado = this.normalizarStatus(this.statusAtual);
    return statusAtualNormalizado !== 'CONCLUIDO' && statusAtualNormalizado !== 'CANCELADO';
  }

  getStatusDisponiveisParaAvanco(): Parametros[] {
    if (!this.statusPermitidos.length) {
      return [];
    }

    if (!this.statusAtual) {
      return [...this.statusPermitidos];
    }

    const statusAtualNormalizado = this.normalizarStatus(this.statusAtual);
    const idxAtual = this.statusPermitidos.findIndex(
      s => this.normalizarStatus(s.descricao) === statusAtualNormalizado
    );

    const candidatos = idxAtual >= 0 ? this.statusPermitidos.slice(idxAtual + 1) : [...this.statusPermitidos];

    return candidatos.filter(
      s => !this.statusJaPassados.has(this.normalizarStatus(s.descricao))
    );
  }

  isUltimoStep(item: HistoricoProducao): boolean {
    if (!this.historico.length) {
      return false;
    }

    const maiorOrdem = Math.max(...this.historico.map(h => h.ordem || 0));
    return item.ordem === maiorOrdem;
  }

  abrirModalAvancarStep(): void {
    if (!this.podeAvancarStep()) {
      this.toastr.warning('A produção já está concluída. Não é possível avançar o step.');
      return;
    }

    this.formNovoStep = {
      idFuncionario: null,
      status: '',
      dataInicio: '',
      dataFim: '',
      observacao: ''
    };

    forkJoin({
      funcionariosCargos: this.cadastroService.getFuncionariosCargos(),
      funcionarios: this.cadastroService.getFuncionarios()
    }).subscribe({
      next: ({ funcionariosCargos, funcionarios }) => {
        this.funcionariosDisponiveis = funcionariosCargos
          .filter(f => this.normalizarStatus(f.setor || '') === 'PRODUCAO')
          .map(f => {
            const idDireto = f.idFuncionario ?? f.id ?? f.codigo;
            if (idDireto !== null && idDireto !== undefined && Number.isFinite(Number(idDireto))) {
              return {
                ...f,
                idFuncionario: Number(idDireto)
              };
            }

            const nomeCargo = this.normalizarStatus(f.cargo || '');
            const nomeFuncionario = this.normalizarStatus(f.nome || '');

            const funcionarioEncontrado = funcionarios.find((func: any) => {
              const nomeCompleto = this.normalizarStatus(`${func.nome || ''} ${func.sobrenome || ''}`);
              const cargoDescricao = this.normalizarStatus(func.cargoDescricao || '');
              const mesmoCargo = cargoDescricao === nomeCargo;
              const nomeCompativel =
                nomeCompleto === nomeFuncionario ||
                nomeCompleto.includes(nomeFuncionario) ||
                nomeFuncionario.includes(this.normalizarStatus(func.nome || ''));

              return mesmoCargo && nomeCompativel;
            });

            return {
              ...f,
              idFuncionario: funcionarioEncontrado?.id
            };
          })
          .filter(
            f =>
              f.idFuncionario !== null &&
              f.idFuncionario !== undefined &&
              Number.isFinite(Number(f.idFuncionario)) &&
              Number(f.idFuncionario) >= 0
          )
          .map(f => ({
            ...f,
            idFuncionario: Number(f.idFuncionario)
          }));

        if (!this.funcionariosDisponiveis.length) {
          this.toastr.warning('Nenhum funcionário do setor PRODUCAO disponível para este step.');
          return;
        }

        if (!this.getStatusDisponiveisParaAvanco().length) {
          this.toastr.warning('Não há próximos status disponíveis para avanço.');
          return;
        }

        this.modalAberto = true;
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao carregar funcionários de produção.');
      }
    });
  }

  fecharModalAvancarStep(): void {
    this.modalAberto = false;
  }

  salvarNovoStep(): void {
    const idFuncionarioNumerico = Number(this.formNovoStep.idFuncionario);
    const idFuncionarioInvalido =
      this.formNovoStep.idFuncionario === null ||
      this.formNovoStep.idFuncionario === undefined ||
      !Number.isFinite(idFuncionarioNumerico) ||
      idFuncionarioNumerico < 0;
    const statusInvalido = !(this.formNovoStep.status || '').trim();
    const dataInicioInvalida = !(this.formNovoStep.dataInicio || '').trim();
    const dataFimInvalida = !(this.formNovoStep.dataFim || '').trim();

    if (idFuncionarioInvalido || statusInvalido || dataInicioInvalida || dataFimInvalida) {
      this.toastr.warning('Preencha funcionário, status, data início e data fim.');
      return;
    }

    const statusSelecionadoValido = this.getStatusDisponiveisParaAvanco()
      .some(s => this.normalizarStatus(s.descricao) === this.normalizarStatus(this.formNovoStep.status));

    if (!statusSelecionadoValido) {
      this.toastr.warning('Status inválido para avanço. Selecione apenas um próximo status disponível.');
      return;
    }

    // Encontrar o status selecionado para obter seu valorNumerico
    const statusSelecionado = this.statusPermitidos.find(
      s => this.normalizarStatus(s.descricao) === this.normalizarStatus(this.formNovoStep.status)
    );

    if (!statusSelecionado || !statusSelecionado.valorNumerico) {
      this.toastr.warning('Erro: Status selecionado não possui ordem definida.');
      return;
    }

    // Validar se data fim é maior que data início
    const dataInicio = new Date(this.formNovoStep.dataInicio);
    const dataFim = new Date(this.formNovoStep.dataFim);

    if (dataFim <= dataInicio) {
      this.toastr.warning('A data fim deve ser maior que a data de início.');
      return;
    }

    const ordemDoStatus = Number(statusSelecionado.valorNumerico);
    const idFuncionario = idFuncionarioNumerico;
    const payload: CriarHistoricoProducao = {
      idProducao: this.idControle,
      idFuncionario,
      status: this.formNovoStep.status,
      dataInicio: `${this.formNovoStep.dataInicio}T08:00:00.000Z`,
      dataFim: `${this.formNovoStep.dataFim}T18:00:00.000Z`,
      observacao: this.formNovoStep.observacao || '',
      ordem: ordemDoStatus
    };

    this.salvandoStep = true;
    this.cadastroService.createHistoricoProducao(payload).subscribe({
      next: () => {
        this.toastr.success('Step avançado com sucesso!');
        this.modalAberto = false;
        this.salvandoStep = false;
        this.loadDados();
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao avançar step de produção.');
        this.salvandoStep = false;
      }
    });
  }

  excluirStep(idHistorico: number): void {
    this.cadastroService.deleteHistoricoProducao(idHistorico).subscribe({
      next: () => {
        this.toastr.success('Step excluído com sucesso!');
        this.loadDados();
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao excluir step de produção.');
      }
    });
  }

  voltar(): void {
    this.router.navigate(['/home', 'producao-controle']);
  }
}